// 직원 관리 관련 유틸리티 함수들
import { Employee } from '../contexts/AppContext';

// 직위 정렬 순서 (단일 정의 — 새 직위 추가 시 여기만 갱신)
export const POSITION_SORT_ORDER: Record<string, number> = { '실장': 0, '팀장': 1, '팀원': 2, '파트': 3 };

// 직원 이름 관련 헬퍼 함수
export const getBaseNameAndSuffix = (name: string): { baseName: string; suffix: string | null } => {
  const match = name.match(/^(.+?)(?:\((\d+)\))?$/);
  if (match) {
    return {
      baseName: match[1],
      suffix: match[2] || null
    };
  }
  return { baseName: name, suffix: null };
};

// 동일한 기본 이름을 가진 직원들 찾기
export const findEmployeesWithSameName = (employees: Employee[], baseName: string): Employee[] => {
  return employees.filter(emp => {
    const { baseName: empBaseName } = getBaseNameAndSuffix(emp.name);
    return empBaseName === baseName;
  });
};

// 새 직원 이름 생성 (중복 시 번호 추가)
export const generateUniqueEmployeeName = (baseName: string, existingEmployees: Employee[]): string => {
  const sameNameEmployees = findEmployeesWithSameName(existingEmployees, baseName);
  
  if (sameNameEmployees.length === 0) {
    return baseName;
  }
  
  // 기존 번호들 추출
  const existingNumbers = sameNameEmployees
    .map(emp => {
      const { suffix } = getBaseNameAndSuffix(emp.name);
      return suffix ? parseInt(suffix) : 0;
    })
    .filter(num => !isNaN(num));
  
  // 다음 번호 찾기
  let nextNumber = 1;
  while (existingNumbers.includes(nextNumber)) {
    nextNumber++;
  }
  
  return `${baseName}(${nextNumber})`;
};

// 직원 데이터 검증
export const validateEmployee = (employee: Partial<Employee>): { valid: boolean; errors: string[] } => {
  const errors: string[] = [];
  
  if (!employee.name || employee.name.trim() === '') {
    errors.push('직원 이름은 필수입니다.');
  }
  
  if (!employee.team || employee.team.trim() === '') {
    errors.push('소속 팀은 필수입니다.');
  }
  
  if (!employee.position) {
    errors.push('직급은 필수입니다.');
  }
  
  if (employee.salary === undefined || employee.salary < 0) {
    errors.push('급여는 0 이상이어야 합니다.');
  }
  
  if (employee.familyCount === undefined || employee.familyCount < 0) {
    errors.push('가족 수는 0 이상이어야 합니다.');
  }
  
  return { valid: errors.length === 0, errors };
};

// 직원 복사 (새 ID 생성)
export const copyEmployee = (employee: Employee, newId: number, newName?: string): Employee => {
  return {
    ...employee,
    id: newId,
    name: newName || employee.name,
    incentive: 0 // 인센티브는 초기화
  };
};

// 직원 정렬
export const sortEmployees = (employees: Employee[], sortBy: 'name' | 'team' | 'position' | 'salary' = 'name'): Employee[] => {
  const sorted = [...employees];
  
  switch(sortBy) {
    case 'name':
      return sorted.sort((a, b) => a.name.localeCompare(b.name, 'ko'));
    case 'team':
      return sorted.sort((a, b) => {
        const teamCompare = a.team.localeCompare(b.team, 'ko');
        if (teamCompare !== 0) return teamCompare;
        return a.name.localeCompare(b.name, 'ko');
      });
    case 'position':
      return sorted.sort((a, b) => {
        const aOrder = POSITION_SORT_ORDER[a.position] ?? 2;
        const bOrder = POSITION_SORT_ORDER[b.position] ?? 2;
        if (aOrder !== bOrder) return aOrder - bOrder;
        return a.name.localeCompare(b.name, 'ko');
      });
    case 'salary':
      return sorted.sort((a, b) => b.salary - a.salary);
    default:
      return sorted;
  }
};

// 직원 필터링
export const filterEmployees = (
  employees: Employee[], 
  filters: {
    team?: string;
    position?: string;
    salaryMin?: number;
    salaryMax?: number;
    searchText?: string;
  }
): Employee[] => {
  return employees.filter(emp => {
    if (filters.team && emp.team !== filters.team) return false;
    if (filters.position && emp.position !== filters.position) return false;
    if (filters.salaryMin !== undefined && emp.salary < filters.salaryMin) return false;
    if (filters.salaryMax !== undefined && emp.salary > filters.salaryMax) return false;
    if (filters.searchText) {
      const searchLower = filters.searchText.toLowerCase();
      return emp.name.toLowerCase().includes(searchLower) ||
             emp.team.toLowerCase().includes(searchLower) ||
             emp.position.toLowerCase().includes(searchLower);
    }
    return true;
  });
};

// 직원 총계 계산
export const calculateEmployeeTotals = (employees: Employee[]) => {
  return employees.reduce((totals, emp) => {
    return {
      totalSalary: totals.totalSalary + emp.salary,
      totalTax: totals.totalTax + emp.tax,
      totalNetSalary: totals.totalNetSalary + emp.netSalary,
      totalIncentive: totals.totalIncentive + emp.incentive,
      totalRetirement: totals.totalRetirement + (emp.retirement || 0),
      totalMeal: totals.totalMeal + (emp.meal || 0),
      totalHousing: totals.totalHousing + (emp.housing || 0),
      totalOvertime: totals.totalOvertime + (emp.overtime || 0),
      totalIndividual: totals.totalIndividual + (emp.individual || 0),
      totalOther: totals.totalOther + (emp.other || 0),
      totalNonTaxable: totals.totalNonTaxable + (emp.nonTaxable || 0)
    };
  }, {
    totalSalary: 0,
    totalTax: 0,
    totalNetSalary: 0,
    totalIncentive: 0,
    totalRetirement: 0,
    totalMeal: 0,
    totalHousing: 0,
    totalOvertime: 0,
    totalIndividual: 0,
    totalOther: 0,
    totalNonTaxable: 0
  });
};